/**
 * client.js — API client para The Crew OS
 *
 * Wrapper sobre fetch que:
 *   - Apunta al backend (proxeado por Vite en dev, VITE_API_URL en prod)
 *   - Inyecta automáticamente `Authorization: Bearer <Firebase ID Token>` si hay sesión
 *   - Fallback a `x-api-key` si está configurado (uso interno / scripts)
 *   - Lanza error descriptivo en respuestas no-ok
 */

import { guardarOffline, leerOffline } from "../lib/offlineCache.js";
import { getAuth } from "firebase/auth";

const BASE = import.meta.env.VITE_API_URL || "";

// v7.0.2 · Helper para URLs de assets servidos por el backend (img src, video src).
// Convierte "/api/foo" → "https://backend.../api/foo" sin tocar URLs ya absolutas.
export function apiUrl(path) {
  if (!path) return path;
  // El backend entrega las miniaturas del feed con la ruta del Portal (exige token del Portal, que el OS no
  // tiene → 401). El OS usa la ruta pública equivalente.
  path = path.replace("/api/portal/feed/preview-image/", "/api/feed/preview-image/");
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith("/api/")) return `${BASE}${path}`;
  return path;
}
const KEY  = import.meta.env.VITE_API_KEY  || "";

/**
 * Obtiene el ID token del usuario logueado en Firebase.
 * Devuelve null si no hay usuario. Token se cachea internamente por Firebase
 * y se refresca automático cuando expira (~1h).
 */
// v8.4.24 · Forzamos `getIdToken(true)` (refresh contra Firebase) UNA VEZ
//   por sesión de tab. Si el user vuelve tras varias horas, el token cacheado
//   de Firebase puede estar próximo a expirar; forzando refresh evitamos que
//   la primera request muera con 401 y tener que hacer round-trip extra.
let _tokenRefreshedThisSession = false;
async function getIdToken() {
  try {
    const user = getAuth().currentUser;
    if (!user) return null;
    if (!_tokenRefreshedThisSession) {
      _tokenRefreshedThisSession = true;
      return await user.getIdToken(true);
    }
    return await user.getIdToken();
  } catch {
    return null;
  }
}

// Fase 76 · el streaming del chat usa fetch (EventSource no manda el token) y necesita el mismo ID token.
export { getIdToken };

async function request(method, path, body, { _retry = 0 } = {}) {
  const uidOffline = getAuth().currentUser?.uid || null;
  const headers = { "Content-Type": "application/json" };

  // Prioridad 1: Firebase Bearer token (frontend con usuario)
  const token = await getIdToken();
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  // Prioridad 2: x-api-key (uso interno / fallback)
  if (KEY) headers["x-api-key"] = KEY;

  let res, networkErr = null;
  try {
    res = await fetch(`${BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    networkErr = e;
  }

  // Retry silencioso para errores de RED o 5xx (hipos Supabase / Cloud Run cold start)
  // 1 reintento, espera 600ms. Solo para GETs y métodos idempotentes.
  if ((networkErr || (res && res.status >= 500 && res.status < 600)) && _retry < 1) {
    const idempotent = method === "GET" || method === "HEAD" || method === "OPTIONS";
    if (idempotent || networkErr) {
      await new Promise(r => setTimeout(r, 600));
      return request(method, path, body, { _retry: _retry + 1 });
    }
  }

  if (networkErr) {
    // Sin red: si es una lectura que ya se guardó, se devuelve lo último guardado (marcado `_offline`).
    if (method === "GET") {
      const guardado = await leerOffline(uidOffline, path);
      if (guardado) {
        try { window.dispatchEvent(new CustomEvent("crew-offline-data", { detail: { ts: guardado.ts } })); } catch { /* sin window */ }
        return { ...guardado.data, _offline: true, _guardado_ts: guardado.ts };
      }
    }
    throw networkErr;
  }

  const data = await res.json().catch(() => ({}));

  // Ventana de mantenimiento: el backend responde 503 + maintenance a quien no está habilitado.
  // Se avisa a la pantalla de mantenimiento (MantenimientoGate) además de lanzar el error normal.
  if (res.status === 503 && (data?.codigo === "mantenimiento" || data?.maintenance)) {
    try { window.dispatchEvent(new CustomEvent("crew-maintenance", { detail: { vuelve: data?.vuelve } })); } catch { /* sin window */ }
  }

  if (!res.ok || data.ok === false) {
    // Si fue 401, probablemente el token expiró. Forzar refresh y reintentar 1 vez.
    if (res.status === 401 && token) {
      const fresh = await getAuth().currentUser?.getIdToken(true).catch(() => null);
      // Si ni siquiera se puede renovar el token, la sesión venció de verdad: página «Tu sesión venció».
      // (Un 401 aislado con token válido NO dispara esto: algunos endpoints responden 401 por otras razones.)
      if (!fresh) { try { window.dispatchEvent(new CustomEvent("crew-session-expired")); } catch { /* sin window */ } }
      if (fresh && fresh !== token) {
        headers["Authorization"] = `Bearer ${fresh}`;
        const retry = await fetch(`${BASE}${path}`, {
          method, headers,
          body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        const retryData = await retry.json().catch(() => ({}));
        if (retry.ok && retryData.ok !== false) return retryData;
      }
    }
    const msg = data.error || `HTTP ${res.status} en ${path}`;
    const err = new Error(msg);
    err.status = res.status;
    if (data.codigo) err.codigo = data.codigo;
    throw err;
  }

  if (method === "GET") guardarOffline(uidOffline, path, data);   // sin await: no demora la respuesta
  return data;
}

import { ESTADOS_CAMBIOS, pedirMotivo } from "../lib/motivoCambio.js";
import { pedirEsfuerzo } from "../lib/esfuerzo.js";

export const api = {
  // Genérico: por si necesitás llamar un endpoint que no está en la lista
  request,

  // ── Tareas ────────────────────────────────────────────────────────────────
  getTareas:           (params = {}) => request("GET", `/api/tareas?${new URLSearchParams(params)}`),
  getTarea:            (id)           => request("GET", `/api/tareas/${id}`),
  getTarea:            (id)          => request("GET", `/api/tareas/${id}`),
  patchTarea: async (id, data) => {
    // Pasar una pieza a «Cliente/EC/QC Cambios» exige dejar escrito qué hay que cambiar (el diseñador lo recibe en su aviso y en el hilo de la pieza).
    if (data?.estado && ESTADOS_CAMBIOS.has(data.estado) && !data.motivo_cambio) {
      const motivo = await pedirMotivo({ estado: data.estado, tareaId: id });
      if (motivo === null) throw new Error("No se cambió el estado");
      if (motivo) data = { ...data, motivo_cambio: motivo };
    }
    let r;
    try {
      r = await request("PATCH", `/api/tareas/${id}`, data);
    } catch (e) {
      // Pasar a «EC Aprobado» una pieza que el cliente ya ve la oculta de su Portal: se pide el motivo y se reintenta.
      if (e?.codigo !== "RETROCESO_OCULTA_AL_CLIENTE") throw e;
      const motivo = await pedirMotivo({ estado: "EC Aprobado", tareaId: id, retroceso: true });
      if (!motivo) throw new Error("No se cambió el estado");
      r = await request("PATCH", `/api/tareas/${id}`, { ...data, motivo_retroceso: motivo });
    }
    // Esfuerzo real: si el backend lo pide (entregó o revisó una pieza), se abre la tarjeta «¿Cuánto te llevó?» sin frenar nada.
    if (r?.pedir_esfuerzo) pedirEsfuerzo(r.pedir_esfuerzo);
    // v6.7.6 F5: emitir toast XP si hubo cambio de estado relevante
    try {
      if (data.estado) {
        const XP_MAP = {
          "Para QC": 10, "QC Aprobado": 10, "EC Aprobado": 10,
          "Enviado Cliente": 15, "Aprobado Cliente": 20,
          "Publicado": 15, "Terminado": 10, "Entregado": 10,
          "Tarea con Cambio": -10, "EC Cambios": -10, "QC Cambios": -10,
          "Cliente Cambios": -15, "Cancelada": -20,
        };
        const delta = XP_MAP[data.estado];
        if (delta) {
          window.dispatchEvent(new CustomEvent("gamif:xp-gain", {
            detail: { delta, descripcion: `→ ${data.estado}` },
          }));
        }
      }
    } catch (_) {}
    return r;
  },
  deleteTarea:         (id, opts={}) => request("DELETE", `/api/tareas/${id}${opts.deleteDrive ? "?delete_drive=1" : ""}`),
  deleteTareasBulk:    (ids)         => request("POST",   `/api/tareas/bulk-delete`, { ids }),
  // Bulk update (estado, responsable, prioridad, fecha, grupo) — jun 2026
  bulkUpdateTareas:    async (ids, updates) => {
    // Mandar piezas a «Cambios» sin decir qué hay que cambiar no pasa (el motivo vale para todas las seleccionadas).
    if (updates?.estado && ESTADOS_CAMBIOS.has(updates.estado) && !updates.motivo_cambio) {
      const motivo = await pedirMotivo({ estado: updates.estado });
      if (motivo === null) throw new Error("No se cambió el estado");
      if (motivo) updates = { ...updates, motivo_cambio: motivo };
    }
    return request("POST", `/api/tareas/bulk-update`, { ids, updates });
  },
  // Reabrir tarea cerrada (CEO/COO/CCC) — jun 2026 v6
  reopenTarea:         (id, opts = {}) => request("POST",  `/api/tareas/${id}/reopen`, opts),
  createTarea:         (data)        => request("POST", `/api/tareas`, data),
  // Quick-time entry: { horas, usuario_id?, descripcion?, fecha? }
  registrarTiempoTarea:(id, data)    => request("POST", `/api/tareas/${id}/time`, data),
  // Re-estimar con IA (force=true sobrescribe)
  estimarTarea:        (id, force=false) => request("POST", `/api/tareas/${id}/estimate${force ? "?force=true" : ""}`, {}),
  estimarMissingBatch: (limit=50)    => request("POST", `/api/tareas/estimate-missing?limit=${limit}`, {}),
  declararEsfuerzo:    (id, horas, etapa = "produccion") => request("POST", `/api/tareas/${id}/esfuerzo`, { horas, etapa }),
  recalibrarTareas:    (limit=100, onlyAboveDefault=false) =>
    request("POST", `/api/tareas/recalibrate?limit=${limit}`, { only_above_default: onlyAboveDefault }),
  reassignTarea:       (id)          => request("POST", `/api/tareas/${id}/reassign`),
  reassignBulk:        (body = {})   => request("POST", `/api/tareas/reassign-bulk`, body),
  getTiempoEnEstados:  (id)          => request("GET",  `/api/tareas/${id}/tiempo-en-estados`),
  getCuellosBotella:   (params = {}) => request("GET",  `/api/analisis/cuellos-de-botella?${new URLSearchParams(params)}`),
  getSesionesAutoTarea:(id)          => request("GET",  `/api/tareas/${id}/sesiones-auto`),
  getCargaUsuario:     (id, params={})=> request("GET", `/api/equipo/${id}/carga?${new URLSearchParams(params)}`),
  getCargaEquipo:      (params = {}) => request("GET",  `/api/equipo/carga?${new URLSearchParams(params)}`),
  getPerformanceEquipo:(dias=30)    => request("GET", `/api/equipo/performance-v2?dias=${dias}`),
  getPerformanceUsuario:(id, dias=30) => request("GET", `/api/equipo/${id}/performance-v2?dias=${dias}`),
  // Equipos físicos / Alquiler
  getEquiposFisicos:    (params={})  => request("GET", `/api/equipos?${new URLSearchParams(params)}`),
  getEquipoFisico:      (id)         => request("GET", `/api/equipos/${id}`),
  createEquipoFisico:   (data)       => request("POST", `/api/equipos`, data),
  updateEquipoFisico:   (id, data)   => request("PATCH", `/api/equipos/${id}`, data),
  deleteEquipoFisico:   (id)         => request("DELETE", `/api/equipos/${id}`),
  getEquiposDashboard:  ()           => request("GET", `/api/equipos/dashboard`),
  getEquiposROI:        (params={})  => request("GET", `/api/equipos/roi?${new URLSearchParams(params)}`),
  getEquiposDisponibles:(params)     => request("GET", `/api/equipos/disponibles?${new URLSearchParams(params)}`),
  getDisponibilidadEquipo:(id, params) => request("GET", `/api/equipos/${id}/disponibilidad?${new URLSearchParams(params)}`),
  // Reservas
  getReservasEquipos:   (params={})  => request("GET", `/api/reservas-equipos?${new URLSearchParams(params)}`),
  createReservaEquipo:  (data)       => request("POST", `/api/reservas-equipos`, data),
  entregarReserva:      (id)         => request("POST", `/api/reservas-equipos/${id}/entregar`, {}),
  devolverReserva:      (id, data)   => request("POST", `/api/reservas-equipos/${id}/devolver`, data),
  updateReservaEquipo:  (id, data)   => request("PATCH", `/api/reservas-equipos/${id}`, data),
  // Mantenimientos
  getMantenimientos:    (params={})  => request("GET", `/api/mantenimientos?${new URLSearchParams(params)}`),
  createMantenimiento:  (data)       => request("POST", `/api/mantenimientos`, data),
  // Partners / Freelancers
  getPartners:           (params={}) => request("GET", `/api/partners?${new URLSearchParams(params)}`),
  getPartner:            (id)        => request("GET", `/api/partners/${id}`),
  createPartner:         (data)      => request("POST", `/api/partners`, data),
  updatePartner:         (id, data)  => request("PATCH", `/api/partners/${id}`, data),
  deletePartner:         (id)        => request("DELETE", `/api/partners/${id}`),
  getPartnersDashboard:  ()          => request("GET", `/api/partners/dashboard`),
  sugerirPartners:       (criterios) => request("POST", `/api/partners/sugerir`, criterios),
  getPartnerAssignments: (params={}) => request("GET", `/api/partner-assignments?${new URLSearchParams(params)}`),
  createPartnerAssignment:(data)     => request("POST", `/api/partner-assignments`, data),
  marcarPartnerEntregado:(id, data)  => request("POST", `/api/partner-assignments/${id}/entregado`, data),
  marcarPartnerPagado:   (id, data)  => request("POST", `/api/partner-assignments/${id}/pagado`, data),
  updatePartnerAssignment:(id, data) => request("PATCH", `/api/partner-assignments/${id}`, data),
  // Influencers
  getInfluencers:        (params={}) => request("GET", `/api/influencers?${new URLSearchParams(params)}`),
  getInfluencer:         (id)        => request("GET", `/api/influencers/${id}`),
  createInfluencer:      (data)      => request("POST", `/api/influencers`, data),
  updateInfluencer:      (id, data)  => request("PATCH", `/api/influencers/${id}`, data),
  deleteInfluencer:      (id)        => request("DELETE", `/api/influencers/${id}`),
  getInfluencersDashboard:()         => request("GET", `/api/influencers/dashboard`),
  sugerirInfluencers:    (criterios) => request("POST", `/api/influencers/sugerir`, criterios),
  getInfluencerContratos:(params={}) => request("GET", `/api/influencer-contratos?${new URLSearchParams(params)}`),
  createInfluencerContrato:(data)    => request("POST", `/api/influencer-contratos`, data),
  updateInfluencerContrato:(id, data)=> request("PATCH", `/api/influencer-contratos/${id}`, data),
  addInfluencerDeliverables:(contratoId, deliverables) => request("POST", `/api/influencer-contratos/${contratoId}/deliverables`, { deliverables }),
  getInfluencerDeliverables:(params={}) => request("GET", `/api/influencer-deliverables?${new URLSearchParams(params)}`),
  createInfluencerDeliverable:(data) => request("POST", `/api/influencer-deliverables`, data),
  updateInfluencerDeliverable:(id, data) => request("PATCH", `/api/influencer-deliverables/${id}`, data),
  registrarMetricasDeliverable:(id, metricas) => request("POST", `/api/influencer-deliverables/${id}/metricas`, metricas),
  // Producciones para terceros
  prodTercerosList:      (params={})    => request("GET",  `/api/producciones-terceros?${new URLSearchParams(params)}`),
  prodTercerosOne:       (id)           => request("GET",  `/api/producciones-terceros/${id}`),
  prodTercerosDashboard: ()             => request("GET",  `/api/producciones-terceros/dashboard`),
  prodTercerosCreate:    (data)         => request("POST", `/api/producciones-terceros`, data),
  prodTercerosUpdate:    (id, data)     => request("PATCH",`/api/producciones-terceros/${id}`, data),
  // Sprint 3: Rentabilidad cliente
  clienteRentabilidad:     (id, dias)      => request("GET",  `/api/clientes/${id}/rentabilidad${dias ? `?dias=${dias}` : ""}`),
  rentabilidadRanking:     (dias)          => request("GET",  `/api/rentabilidad/ranking${dias ? `?dias=${dias}` : ""}`),
  // Modo evento
  clienteModoEvento:       (id, data)      => request("POST", `/api/clientes/${id}/modo-evento`, data),
  checkModoEventoOff:      ()              => request("POST", `/api/clientes/check-modo-evento`, {}),
  // Briefs del Plan
  briefsPlanList:          (params={})     => request("GET",  `/api/briefs-plan?${new URLSearchParams(params)}`),
  briefsPlanCreate:        (data)          => request("POST", `/api/briefs-plan`, data),
  briefsPlanUpdate:        (id, data)      => request("PATCH",`/api/briefs-plan/${id}`, data),
  briefsPlanEnviar:        (id)            => request("POST", `/api/briefs-plan/${id}/enviar-a-com`, {}),
  // Generador de Contenido
  contentPlansList:    (params={}) => request("GET",  `/api/content-plans?${new URLSearchParams(params)}`),
  contentPlanOne:      (id)        => request("GET",  `/api/content-plans/${id}`),
  contentPlanCreate:   (data)      => request("POST", `/api/content-plans`, data),
  contentPlanUpdate:   (id, data)  => request("PATCH",`/api/content-plans/${id}`, data),
  contentPlanGenerate: (id)        => request("POST", `/api/content-plans/${id}/generate`, {}),
  contentPlanPresentacion: (id, media_kit) => request("POST", `/api/content-plans/${id}/presentacion`, { media_kit }),
  contentPlanAprobar:  (id)        => request("POST", `/api/content-plans/${id}/aprobar`, {}),
  contentPlanConvert:  (id, posteos, useDoc, fuente) => request("POST", `/api/content-plans/${id}/convert-to-tasks`, { posteos, useDoc, fuente }),
  contentPlanRegenerarDoc: (id) => request("POST", `/api/content-plans/${id}/regenerar-doc`, {}),
  contentPlanCancelar: (id)        => request("DELETE",`/api/content-plans/${id}`),
  contentPlanMessages: (id)        => request("GET",  `/api/content-plans/${id}/messages`),
  contentPlanChat:     (id, mensaje) => request("POST", `/api/content-plans/${id}/chat`, { mensaje }),
  // Reglas de la cuenta que el generador aprende de las correcciones (Fase 77)
  contentPlanReglas:           (id)                 => request("GET",  `/api/content-plans/${id}/reglas`),
  contentPlanReglaCrear:       (id, regla, categoria) => request("POST", `/api/content-plans/${id}/reglas`, { regla, categoria }),
  contentPlanReglaConfirmar:   (id, reglaId, regla) => request("POST", `/api/content-plans/${id}/reglas/${reglaId}/confirmar`, { regla }),
  contentPlanReglaDescartar:   (id, reglaId)        => request("POST", `/api/content-plans/${id}/reglas/${reglaId}/descartar`, {}),
  // Convertir plan legacy (Doc viejo) → plan canónico OS con un click
  contentPlanConvertLegacy: (data) => request("POST", `/api/content-plans/convert-legacy`, data),
  // Portal Cliente · gestión desde OS interno
  portalUsersList:      (clienteId)         => request("GET",  `/api/clientes/${clienteId}/portal-users`),
  portalUserInvite:     (clienteId, data)   => request("POST", `/api/clientes/${clienteId}/portal-users`, data),
  portalUserUpdate:     (clienteId, userId, data) => request("PATCH", `/api/clientes/${clienteId}/portal-users/${userId}`, data),
  portalUserRemove:     (clienteId, userId) => request("DELETE", `/api/clientes/${clienteId}/portal-users/${userId}`),
  portalUserMagicLink:  (clienteId, userId) => request("POST", `/api/clientes/${clienteId}/portal-users/${userId}/magic-link`, {}),
  portalUserSetPassword:(clienteId, userId, password) => request("POST", `/api/clientes/${clienteId}/portal-users/${userId}/set-password`, { password }),
  portalConfig:         (clienteId, data)   => request("PATCH", `/api/clientes/${clienteId}/portal-config`, data),
  // Portal Documentos (Google native: Docs/Sheets/Slides)
  documentosList:       (clienteId)         => request("GET",  `/api/clientes/${clienteId}/documentos`),
  documentoCrear:       (clienteId, data)   => request("POST", `/api/clientes/${clienteId}/documentos/crear`, data),
  documentoLinkear:     (clienteId, data)   => request("POST", `/api/clientes/${clienteId}/documentos/linkear`, data),
  documentoUpdate:      (id, data)          => request("PATCH",`/api/portal-documentos/${id}`, data),
  documentoEliminar:    (id, borrarEnDrive) => request("DELETE",`/api/portal-documentos/${id}${borrarEnDrive ? "?borrarEnDrive=1" : ""}`),
  documentoPublicar:    (id, publicar)      => request("PATCH",`/api/portal-documentos/${id}/publicar`, { publicar }),
  // Búsqueda semántica producciones + tareas con material
  produccionesSearch:   (params={})         => request("GET", `/api/producciones-search?${new URLSearchParams(params)}`),
  produccionesListado:  (clienteId, params={}) => request("GET", `/api/clientes/${clienteId}/producciones-listado?${new URLSearchParams(params)}`),
  embeddingsReindex:    ()                  => request("POST", `/api/embeddings/reindex`, {}),
  // Mi Día + Apelaciones
  miDia:                   (fecha)         => request("GET",  `/api/mi-dia${fecha ? `?fecha=${fecha}` : ""}`),
  miDiaPipeline:           ()              => request("GET",  `/api/mi-dia/pipeline`),
  miDiaUser:               (userId, fecha) => request("GET",  `/api/mi-dia/${userId}${fecha ? `?fecha=${fecha}` : ""}`),
  miDiaArmar:              (data)          => request("POST", `/api/mi-dia/armar`, data || {}),
  miDiaCerrar:             (data)          => request("POST", `/api/mi-dia/cerrar`, data || {}),
  miDiaCierre:             (fecha)         => request("GET",  `/api/mi-dia/cierre${fecha ? `?fecha=${fecha}` : ""}`),
  miDiaCierreTardio:       ()              => request("GET",  `/api/mi-dia/cierre-tardio`),
  miDiaCerrarConMotivos:   (data)          => request("POST", `/api/mi-dia/cierre`, data || {}),
  // El trabajo de cada persona: libro, registro rápido y cartera del EC
  trabajoLibro:            (params = {})   => request("GET",  `/api/trabajo/libro?${new URLSearchParams(params)}`),
  trabajoEquipo:           (params = {})   => request("GET",  `/api/trabajo/equipo?${new URLSearchParams(params)}`),
  trabajoRegistroTipos:    ()              => request("GET",  `/api/trabajo/registro/tipos`),
  trabajoRegistroCrear:    (data)          => request("POST", `/api/trabajo/registro`, data || {}),
  trabajoRegistros:        (params = {})   => request("GET",  `/api/trabajo/registro?${new URLSearchParams(params)}`),
  trabajoRegistroBorrar:   (id)            => request("DELETE", `/api/trabajo/registro/${id}`),
  carteraEC:               (ecId)          => request("GET",  `/api/cartera${ecId ? `?ec=${ecId}` : ""}`),
  cuentasCOM:              (comId)         => request("GET",  `/api/cuentas-com${comId ? `?com=${comId}` : ""}`),
  esfuerzoPendiente:       ()              => request("GET",  "/api/trabajo/esfuerzo-pendiente"),
  trabajoHoja:             (params = {})   => request("GET",  `/api/trabajo/hoja?${new URLSearchParams(params)}`),
  // Espacios de trabajo (Fase 84): lo que no es una cuenta de cliente. Ver src/routes/espacios.js del backend.
  espacios:                (p = {})        => request("GET",  `/api/espacios?${new URLSearchParams(p)}`),
  espaciosMios:            ()              => request("GET",  "/api/espacios/mios"),
  espaciosTipos:           ()              => request("GET",  "/api/espacios/tipos"),
  espacioCrear:            (d)             => request("POST", "/api/espacios", d || {}),
  espacioPropuesta:        (d)             => request("POST", "/api/espacios/propuesta", d || {}),
  espacioPropuestaCanal:   (conv)          => request("GET",  `/api/espacios/propuesta-canal/${conv}`),
  espacioDesdeCanal:       (conv, d)       => request("POST", `/api/espacios/desde-canal/${conv}`, d || {}),
  espacioParecidos:        (q)             => request("GET",  `/api/espacios/parecidos?q=${encodeURIComponent(q)}`),
  espacioCarga:            (persona)       => request("GET",  `/api/espacios/carga/${persona}`),
  espacio:                 (id)            => request("GET",  `/api/espacios/${id}`),
  espacioEditar:           (id, d)         => request("PATCH", `/api/espacios/${id}`, d || {}),
  espacioEstado:           (id, d)         => request("POST", `/api/espacios/${id}/estado`, d || {}),
  espacioMiembroAgregar:   (id, d)         => request("POST", `/api/espacios/${id}/miembros`, d || {}),
  espacioMiembroQuitar:    (id, persona)   => request("DELETE", `/api/espacios/${id}/miembros/${persona}`),
  espacioPasarDueno:       (id, persona)   => request("POST", `/api/espacios/${id}/dueno`, { persona_id: persona }),
  espacioEntrar:           (id)            => request("POST", `/api/espacios/${id}/entrar`, {}),
  espacioSalir:            (id)            => request("POST", `/api/espacios/${id}/salir`, {}),
  espacioHitoCrear:        (id, d)         => request("POST", `/api/espacios/${id}/hitos`, d || {}),
  espacioHitoEditar:       (id, h, d)      => request("PATCH", `/api/espacios/${id}/hitos/${h}`, d || {}),
  espacioHitoBorrar:       (id, h)         => request("DELETE", `/api/espacios/${id}/hitos/${h}`),
  espacioHitoCumplir:      (id, h, c = true) => request("POST", `/api/espacios/${id}/hitos/${h}/cumplir`, { cumplido: c }),
  espacioTareas:           (id, hito)      => request("GET",  `/api/espacios/${id}/tareas${hito ? `?hito=${hito}` : ""}`),
  espacioLinea:            (id)            => request("GET",  `/api/espacios/${id}/linea-de-tiempo`),
  espacioDecisiones:       (id)            => request("GET",  `/api/espacios/${id}/decisiones`),
  espacioDecisionCrear:    (id, d)         => request("POST", `/api/espacios/${id}/decisiones`, d || {}),
  espacioCheckin:          (id)            => request("GET",  `/api/espacios/${id}/checkin`),
  espacioCheckinConfirmar: (id, d)         => request("POST", `/api/espacios/${id}/checkin`, d || {}),
  espacioCheckins:         (id)            => request("GET",  `/api/espacios/${id}/checkins`),
  espacioIndicadores:      (id)            => request("GET",  `/api/espacios/${id}/indicadores`),
  espacioIndicadorCrear:   (id, d)         => request("POST", `/api/espacios/${id}/indicadores`, d || {}),
  espacioIndicadorValor:   (id, i, d)      => request("PUT",  `/api/espacios/${id}/indicadores/${i}/valor`, d || {}),
  espacioIndicadorBorrar:  (id, i)         => request("DELETE", `/api/espacios/${id}/indicadores/${i}`),
  espacioBloqueos:         (id)            => request("GET",  `/api/espacios/${id}/bloqueos`),
  espacioBloqueoCrear:     (id, d)         => request("POST", `/api/espacios/${id}/bloqueos`, d || {}),
  espacioBloqueoResolver:  (id, c, nota)   => request("POST", `/api/espacios/${id}/bloqueos/${c}/resolver`, { nota }),
  espacioSolicitudes:      (id)            => request("GET",  `/api/espacios/${id}/solicitudes`),
  espacioSolicitudCrear:   (id, d)         => request("POST", `/api/espacios/${id}/solicitudes`, d || {}),
  espacioSolicitudResponder: (id, s, d)    => request("POST", `/api/espacios/${id}/solicitudes/${s}/responder`, d || {}),
  espacioSolicitudesMias:  ()              => request("GET",  "/api/espacios/solicitudes/mias"),
  espacioFuentes:          (id)            => request("GET",  `/api/espacios/${id}/fuentes`),
  espacioFuenteCrear:      (id, d)         => request("POST", `/api/espacios/${id}/fuentes`, d || {}),
  espacioFuenteCambiar:    (id, f, d)      => request("PATCH", `/api/espacios/${id}/fuentes/${f}`, d || {}),
  espacioFuenteSacar:      (id, f)         => request("DELETE", `/api/espacios/${id}/fuentes/${f}`),
  espacioContexto:         (id, refrescar) => request("GET",  `/api/espacios/${id}/contexto${refrescar ? "?refrescar=1" : ""}`),
  espacioPreguntar:        (id, pregunta)  => request("POST", `/api/espacios/${id}/preguntar`, { pregunta }),
  presentacionCompartirGrupo: (grupoId, compartir) => request("POST", `/api/grupos/${grupoId}/presentacion/compartir`, { compartir }),
  presentacionCompartirPlan:  (planId, compartir)  => request("POST", `/api/content-plans/${planId}/presentacion/compartir`, { compartir }),
  cuentasMetricas:         (p = {})        => request("GET",  `/api/cuentas/metricas?${new URLSearchParams(p)}`),
  cuentaResumen:           (id)            => request("GET",  `/api/cuentas/${id}/resumen`),
  cuentaScorecard:         (id)            => request("GET",  `/api/cuentas/${id}/scorecard`),
  cuentasMetricasDefinicion: ()            => request("GET",  "/api/cuentas/metricas/definicion"),
  cuentasMetricasAjuste:   (clave, valor)  => request("PUT",  `/api/cuentas/metricas/ajustes/${clave}`, { valor }),
  // Reporte mensual del cliente: se arma con lo medido, lo revisa una persona y recién ahí lo ve el cliente
  reportesClientes:        (p = {})        => request("GET",  `/api/reportes-clientes?${new URLSearchParams(p)}`),
  reporteClienteGet:       (id)            => request("GET",  `/api/reportes-clientes/${id}`),
  reporteClienteGenerar:   (body)          => request("POST", "/api/reportes-clientes/generar", body),
  reporteClienteEditar:    (id, body)      => request("PATCH", `/api/reportes-clientes/${id}`, body),
  reporteClientePublicar:  (id, publicar)  => request("POST", `/api/reportes-clientes/${id}/publicar`, { publicar }),
  reporteClientePdf:       (id)            => request("GET",  `/api/reportes-clientes/${id}/pdf`),
  espacioReuniones:        (id)            => request("GET",  `/api/espacios/${id}/reuniones`),
  espacioReunionCrear:     (id, d)         => request("POST", `/api/espacios/${id}/reuniones`, d || {}),
  espacioReunionCambiar:   (id, r, d)      => request("PATCH", `/api/espacios/${id}/reuniones/${r}`, d || {}),
  espacioActaPropuesta:    (id, r, d)      => request("POST", `/api/espacios/${id}/reuniones/${r}/acta/propuesta`, d || {}),
  espacioActaConfirmar:    (id, r, d)      => request("POST", `/api/espacios/${id}/reuniones/${r}/acta`, d || {}),
  chatDecision:            (msgId, d)      => request("POST", `/api/chat/mensajes/${msgId}/decision`, d || {}),
  chatTareaBorrador:       (msgId)         => request("GET",  `/api/chat/mensajes/${msgId}/tarea-borrador`),
  chatTarjetas:            (refs)          => request("GET",  `/api/chat/tarjetas?refs=${encodeURIComponent(refs.join(","))}`),
  miDiaApelar:             (data)          => request("POST", `/api/mi-dia/apelar`, data),
  apelacionesList:         (params={})     => request("GET",  `/api/apelaciones?${new URLSearchParams(params)}`),
  apelacionGet:            (id)            => request("GET",  `/api/apelaciones/${id}`),  // v6.5 — detalle + tareas
  apelacionDecidir:        (id, data)      => request("POST", `/api/apelaciones/${id}/decidir`, data),
  // NPS
  npsDashboard:          (params={})     => request("GET",  `/api/nps/dashboard/_agg?${new URLSearchParams(params)}`),
  npsEnviarMes:          ()              => request("POST", `/api/nps/enviar-mes`, {}),
  // Contratos · renovación
  contratosRenovacionCheck: ()           => request("POST", `/api/contratos/renovacion-check`, {}),
  // EC (Ejecutivo de Cuentas) — metas + cockpit
  ecMetasList:           (params={})     => request("GET",  `/api/ec/metas?${new URLSearchParams(params)}`),
  ecMetaUpsert:          (data)          => request("POST", `/api/ec/metas`, data),
  ecMetaDelete:          (id)            => request("DELETE",`/api/ec/metas/${id}`),
  ecCockpit:             (usuario_id)    => request("GET",  `/api/ec/cockpit?usuario_id=${usuario_id}`),
  ecCumplimiento:        (usuario_id, anio, mes) =>
                                            request("GET", `/api/ec/cumplimiento-meta?usuario_id=${usuario_id}&anio=${anio||""}&mes=${mes||""}`),
  // Búsqueda semántica (B9 banco de recursos AI)
  semanticSearch:          (query, opts={}) => request("POST", `/api/search/semantic`, { query, ...opts }),
  semanticReindexTareas:   ()              => request("POST", `/api/search/reindex-tareas`, {}),
  semanticIndexTarea:      (id)            => request("POST", `/api/search/index-tarea/${id}`, {}),
  // Health checks
  healthCliente:           (id, dias)       => request("GET",  `/api/health/cliente/${id}${dias ? `?dias=${dias}` : ""}`),
  healthCapacityECs:       ()               => request("GET",  `/api/health/capacity-ecs`),

  // v6.4 · Avatar de usuario (multipart)
  uploadMyAvatar: async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const token = await getIdToken();
    const res = await fetch(`${BASE}/api/equipo/me/foto`, {
      method:  "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body:    fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  },
  deleteMyAvatar: () => request("DELETE", `/api/equipo/me/foto`),
  uploadAvatarFor: async (userId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const token = await getIdToken();
    const res = await fetch(`${BASE}/api/equipo/${userId}/foto`, {
      method:  "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body:    fd,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.ok === false) throw new Error(data.error || `HTTP ${res.status}`);
    return data;
  },
  // v6.4 · Cumplimiento drill-down + admin
  cumplimientoEventos:        (userId, dias=30) => request("GET", `/api/cumplimiento/eventos/${userId}?dias=${dias}`),
  cumplimientoArmarBaseline:  (body={})         => request("POST", `/api/admin/cumplimiento/armar-baseline`, body),
  cumplimientoResetUser:      (userId, body={}) => request("POST", `/api/admin/cumplimiento/reset/${userId}`, body),
  cumplimientoCorrerDetector: ()                => request("POST", `/api/admin/cumplimiento/correr-detector`, {}),
  healthFueraDePlan:       (dias)           => request("GET",  `/api/health/fuera-de-plan${dias ? `?dias=${dias}` : ""}`),
  // Smart assigner
  smartAssignTarea:        (id)              => request("POST", `/api/tareas/${id}/asignar-smart`, {}),
  candidatosAsignacion:    (id)              => request("GET",  `/api/tareas/${id}/candidatos-asignacion`),
  smartAssignAllUnassigned:()                => request("POST", `/api/tareas/asignar-todas-sin-asignar`, {}),
  cargasEquipo:            ()                => request("GET",  `/api/equipo/cargas`),
  cargaUser:               (id)              => request("GET",  `/api/equipo/${id}/carga`),
  // Feedback cards
  feedbackList:            (params={})       => request("GET",  `/api/feedback-cards?${new URLSearchParams(params)}`),
  feedbackCreate:          (data)            => request("POST", `/api/feedback-cards`, data),
  feedbackMarcarLeido:     (id)              => request("PATCH",`/api/feedback-cards/${id}/marcar-leido`, {}),
  feedbackResponder:       (id, respuesta)   => request("PATCH",`/api/feedback-cards/${id}/responder`, { respuesta }),
  feedbackResolver:        (id)              => request("PATCH",`/api/feedback-cards/${id}/resolver`, {}),
  // Producción / dependencia
  produccionGenerarFicha:  (id)              => request("POST", `/api/producciones/${id}/ficha-tecnica/generar`, {}),
  tareaDependencias:       (id)              => request("GET",  `/api/tareas/${id}/dependencias`),
  // Comments / chat interno
  // v8.6.0 · audiencia opcional: "interno" | "cliente". Sin el parametro el
  //   backend devuelve todo (compat con vistas de actividad/historial).
  commentsList:            (entidad, entidadId, audiencia) =>
    request("GET", `/api/comments/${entidad}/${entidadId}` + (audiencia ? `?audiencia=${audiencia}` : "")),
  commentCreate:           (data)               => request("POST", `/api/comments`, data),
  commentUpdate:           (id, data)           => request("PATCH",`/api/comments/${id}`, data),
  commentDelete:           (id, datos)          => request("DELETE",`/api/comments/${id}`, datos),   // Fase 79: se retira con motivo, no se borra
  commentsCount:           (entidad, entidadId) => request("GET",  `/api/comments/${entidad}/${entidadId}/count`),
  commentReact:            (id, emoji)          => request("POST", `/api/comments/${id}/react`, { emoji }),
  // Adjuntar archivo desde Drive (pegar URL) — no re-sube, solo extrae metadata
  commentAttachFromDrive: (entidad, entidadId, url) =>
    request("POST", "/api/comments/attach-from-drive", { entidad, entidad_id: entidadId, url }),

  // ── Importer por cliente ───────────────────────────────────────────────
  // Sube xlsx (multipart) — devuelve { ok, import_run_id, skipped, created, ... }
  clienteImportXlsx: async (clienteId, file, opts = {}) => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    const token = await getIdToken();
    const url = (import.meta.env.VITE_API_URL || "") +
      `/api/clientes/${clienteId}/import${opts.dryRun ? "?dry_run=1" : ""}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || `Import falló (${res.status})`);
    }
    const data = await res.json();
    if (data && data.ok === false) throw new Error(data.error || "Import falló");
    return data;
  },
  clienteImportSheets: (clienteId, sheetsUrl, opts = {}) =>
    request("POST", `/api/clientes/${clienteId}/import${opts.dryRun ? "?dry_run=1" : ""}`, { sheets_url: sheetsUrl }),
  importsList:    (clienteId) => request("GET", `/api/imports${clienteId ? `?cliente_id=${clienteId}` : ""}`),
  importRollback: (runId)     => request("POST", `/api/imports/${runId}/rollback`, {}),
  // Adjuntos: sube 1 archivo y devuelve metadata { type, url, drive_id, name, mime, size, thumbnail_url }
  commentUploadAttachment: async (entidad, entidadId, file) => {
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("entidad", entidad);
    fd.append("entidad_id", entidadId);
    const token = await getIdToken();
    const url = (import.meta.env.VITE_API_URL || "") + "/api/comments/upload";
    const res = await fetch(url, {
      method: "POST",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: fd,
    });
    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(t || `Upload falló (${res.status})`);
    }
    const data = await res.json();
    // handle() wrapper devuelve 200 con { ok: false, error } cuando algo falló
    if (data && data.ok === false) {
      throw new Error(data.error || "Upload falló (backend)");
    }
    return data;
  },
  // Workflow multi-rol
  workflowTemplates:       ()              => request("GET",  `/api/workflow/templates`),
  workflowInicializar:     (tareaId, tipo) => request("POST", `/api/tareas/${tareaId}/workflow/inicializar`, { workflow_tipo: tipo }),
  workflowAvanzar:         (tareaId)       => request("POST", `/api/tareas/${tareaId}/workflow/avanzar`, {}),
  workflowActualizarPaso:  (tareaId, pasoIdx, data) => request("PATCH", `/api/tareas/${tareaId}/workflow/paso/${pasoIdx}`, data),
  // Inbox de pedidos del cliente
  pedidosClienteList:      (params={}) => request("GET",  `/api/pedidos-cliente?${new URLSearchParams(params)}`),
  pedidosClienteOne:       (id)        => request("GET",  `/api/pedidos-cliente/${id}`),
  pedidosClienteDashboard: ()          => request("GET",  `/api/pedidos-cliente/dashboard`),
  pedidosClienteCreate:    (data)      => request("POST", `/api/pedidos-cliente`, data),
  pedidosClienteUpdate:    (id, data)  => request("PATCH",`/api/pedidos-cliente/${id}`, data),
  pedidosClienteConvertir: (id, data)  => request("POST", `/api/pedidos-cliente/${id}/convertir-tarea`, data),
  pedidosClienteTranscribir:(id)       => request("POST", `/api/pedidos-cliente/${id}/transcribir`, {}),
  // v7.6.7 Sprint Agencia
  pedidosClienteAnalyticsWidget:()     => request("GET",  `/api/pedidos-cliente/analytics-widget`),
  pedidosClienteColaPriorizada: (limit=30) => request("GET", `/api/pedidos-cliente/cola-priorizada?limit=${limit}`),
  // Score de cumplimiento
  cumplimientoMiScore:    (dias)         => request("GET",  `/api/cumplimiento/mi-score${dias ? `?dias=${dias}` : ""}`),
  cumplimientoScore:      (userId, dias) => request("GET",  `/api/cumplimiento/score/${userId}${dias ? `?dias=${dias}` : ""}`),
  cumplimientoRanking:    (dias)         => request("GET",  `/api/cumplimiento/ranking${dias ? `?dias=${dias}` : ""}`),
  // GHL Sync (bidireccional)
  ghlSyncCliente:        (id)           => request("POST", `/api/ghl/sync-cliente/${id}`, {}),
  ghlSyncLead:           (id)           => request("POST", `/api/ghl/sync-lead/${id}`, {}),
  ghlSyncAll:            (incluirProspectos) => request("POST", `/api/ghl/sync-all`, { incluir_prospectos: !!incluirProspectos }),
  // Media Buying as a Service
  mediaPackagesList:     (params={})    => request("GET",  `/api/media-packages?${new URLSearchParams(params)}`),
  mediaPackagesCreate:   (data)         => request("POST", `/api/media-packages`, data),
  mediaPackagesUpdate:   (id, data)     => request("PATCH",`/api/media-packages/${id}`, data),
  mediaContractsList:    (params={})    => request("GET",  `/api/media-contracts?${new URLSearchParams(params)}`),
  mediaContractsCreate:  (data)         => request("POST", `/api/media-contracts`, data),
  mediaContractsUpdate:  (id, data)     => request("PATCH",`/api/media-contracts/${id}`, data),
  mediaBuyingDashboard:  ()             => request("GET",  `/api/media-buying/dashboard`),
  // Live Events / Activaciones
  liveEventsList:        (params={})    => request("GET",  `/api/live-events?${new URLSearchParams(params)}`),
  liveEventsOne:         (id)           => request("GET",  `/api/live-events/${id}`),
  liveEventsDashboard:   ()             => request("GET",  `/api/live-events/dashboard`),
  liveEventsCreate:      (data)         => request("POST", `/api/live-events`, data),
  liveEventsUpdate:      (id, data)     => request("PATCH",`/api/live-events/${id}`, data),
  liveEventsDelete:      (id)           => request("DELETE",`/api/live-events/${id}`),
  // Report subscriptions
  reportSubsList:        ()             => request("GET",  `/api/report-subscriptions`),
  reportSubsAll:         ()             => request("GET",  `/api/report-subscriptions/all`),
  reportSubsCreate:      (data)         => request("POST", `/api/report-subscriptions`, data),
  reportSubsUpdate:      (id, data)     => request("PATCH",`/api/report-subscriptions/${id}`, data),
  reportSubsDelete:      (id)           => request("DELETE",`/api/report-subscriptions/${id}`),
  reportSubsTest:        (id)           => request("POST", `/api/report-subscriptions/${id}/test`, {}),
  reportSubsTipos:       ()             => request("GET",  `/api/report-subscriptions/tipos`),
  // Presupuestos anuales
  presupuestosList:        (params={}) => request("GET",  `/api/presupuestos?${new URLSearchParams(params)}`),
  presupuestosCreate:      (data)      => request("POST", `/api/presupuestos`, data),
  presupuestosUpdate:      (id, data)  => request("PATCH",`/api/presupuestos/${id}`, data),
  presupuestosDelete:      (id)        => request("DELETE",`/api/presupuestos/${id}`),
  presupuestosComparativo: (anio)      => request("GET",  `/api/presupuestos/comparativo?anio=${anio || ""}`),
  // Crew Up Circles
  crewUpCircles:           (params={}) => request("GET",  `/api/crew-up/circles?${new URLSearchParams(params)}`),
  crewUpCircle:            (id)        => request("GET",  `/api/crew-up/circles/${id}`),
  crewUpCircleCreate:      (data)      => request("POST", `/api/crew-up/circles`, data),
  crewUpCircleUpdate:      (id, data)  => request("PATCH",`/api/crew-up/circles/${id}`, data),
  crewUpCircleJoin:        (id, userId)=> request("POST", `/api/crew-up/circles/${id}/join`, { usuario_id: userId }),
  crewUpCircleLeave:       (id, userId)=> request("POST", `/api/crew-up/circles/${id}/leave`, { usuario_id: userId }),
  crewUpCircleAttended:    (id, asistentes) => request("POST", `/api/crew-up/circles/${id}/attended`, { asistentes }),
  // Learning Paths
  crewUpPaths:             (params={}) => request("GET",  `/api/crew-up/paths?${new URLSearchParams(params)}`),
  crewUpPath:              (id)        => request("GET",  `/api/crew-up/paths/${id}`),
  crewUpPathCreate:        (data)      => request("POST", `/api/crew-up/paths`, data),
  crewUpPathUpdate:        (id, data)  => request("PATCH",`/api/crew-up/paths/${id}`, data),
  crewUpPathProgreso:      (id, userId)=> request("GET",  `/api/crew-up/paths/${id}/progreso/${userId}`),
  // Crew Up (Academia)
  crewUpPilares:       ()             => request("GET", `/api/crew-up/pilares`),
  crewUpContent:       (params={})    => request("GET", `/api/crew-up/content?${new URLSearchParams(params)}`),
  crewUpContentOne:    (id)           => request("GET", `/api/crew-up/content/${id}`),
  crewUpContentCreate: (data)         => request("POST", `/api/crew-up/content`, data),
  crewUpContentUpdate: (id, data)     => request("PATCH", `/api/crew-up/content/${id}`, data),
  crewUpProgress:      (data)         => request("POST", `/api/crew-up/progress`, data),
  crewUpPulse:         (data)         => request("POST", `/api/crew-up/pulse`, data),
  crewUpPulsesUser:    (usuarioId)    => request("GET", `/api/crew-up/pulses/${usuarioId}`),
  crewUpPulsesEquipo:  ()             => request("GET", `/api/crew-up/pulses-equipo`),
  crewUpKudo:          (data)         => request("POST", `/api/crew-up/kudo`, data),
  crewUpKudos:         (params={})    => request("GET", `/api/crew-up/kudos?${new URLSearchParams(params)}`),
  crewUpQuestsUser:    (usuarioId)    => request("GET", `/api/crew-up/quests/${usuarioId}`),
  crewUpQuestCreate:   (data)         => request("POST", `/api/crew-up/quests`, data),
  crewUpQuestUpdate:   (id, data)     => request("PATCH", `/api/crew-up/quests/${id}`, data),
  crewUpMi:            ()             => request("GET", `/api/crew-up/mi`),
  crewUpDailyDrop:     ()             => request("GET", `/api/crew-up/daily-drop`),
  crewUpDashboardEquipo:()            => request("GET", `/api/crew-up/dashboard-equipo`),
  // OKRs
  getOkrs:             ()             => request("GET", `/api/okrs`),
  getOkrsShadow:       (codigo)       => request("GET", `/api/okrs/${codigo}`),
  updateOkrProgreso:   (codigo, idx, data) => request("PUT", `/api/okrs/${codigo}/${idx}/progreso`, data),
  // Reports generators
  generarReporteFiscalPY:    (periodo, formato="ambos") => request("POST", `/api/reports/fiscal-py`, { periodo, formato }),
  generarReporteCashflow:    (formato="ambos") => request("POST", `/api/reports/cashflow`, { formato }),
  generarReportePipeline:    (formato="ambos") => request("POST", `/api/reports/pipeline-comercial`, { formato }),
  generarReporteCapacidad:   (formato="pdf")   => request("POST", `/api/reports/capacidad-equipo`, { formato }),
  generarReporteBoardPack:   (periodo)         => request("POST", `/api/reports/board-pack-ceo`, { periodo }),
  generarReporteAICost:      (periodo, formato="ambos") => request("POST", `/api/reports/ai-cost`, { periodo, formato }),
  generarReporteQBR:         (clienteId, trimestre, formato="gslides") => request("POST", `/api/reports/qbr-cliente`, { clienteId, trimestre, formato }),

  // ── Administrativo-Financiero ─────────────────────────────────────────────
  // Tipo de cambio
  getTriage:              (params = {}) => request("GET", `/api/triage?${new URLSearchParams(params)}`),
  triageAccion:           (id, accion, payload = {}) => request("POST", `/api/triage/${encodeURIComponent(id)}/accion`, { accion, payload }),
  getVistasCompartidas:  (scope = "tareas") => request("GET", `/api/vistas?scope=${scope}`),
  crearVistaCompartida:   (nombre, filtros, scope = "tareas") => request("POST", "/api/vistas", { scope, nombre, filtros }),
  borrarVistaCompartida:  (id) => request("DELETE", `/api/vistas/${encodeURIComponent(id)}`),
  getFlags:               () => request("GET", "/api/config/flags"),
  setFlag:                (key, activo) => request("PATCH", `/api/config/flags/${key}`, { activo }),
  getFeeVivo:             (clienteId, mes) => request("GET", `/api/clientes/${clienteId}/fee${mes ? `?mes=${mes}` : ""}`),
  getAccesoEstado:        () => request("GET", "/api/acceso/estado"),
  getTipoCambioActual:    (moneda = "USD") => request("GET", `/api/tipo-cambio/actual?moneda=${moneda}`),
  getTipoCambioHistorico: (params = {})    => request("GET", `/api/tipo-cambio/historico?${new URLSearchParams(params)}`),
  // Contactos
  getContactos:           (params = {})    => request("GET", `/api/contactos?${new URLSearchParams(params)}`),
  createContacto:         (data)           => request("POST", `/api/contactos`, data),
  updateContacto:         (id, data)       => request("PATCH", `/api/contactos/${id}`, data),
  // Prestaciones
  getPrestaciones:        ()               => request("GET", `/api/prestaciones`),
  createPrestacion:       (data)           => request("POST", `/api/prestaciones`, data),
  updatePrestacion:       (id, data)       => request("PATCH", `/api/prestaciones/${id}`, data),
  // Contratos
  getContratos:           (params = {})    => request("GET", `/api/contratos?${new URLSearchParams(params)}`),
  getContrato:            (id)             => request("GET", `/api/contratos/${id}`),
  createContrato:         (data)           => request("POST", `/api/contratos`, data),
  updateContrato:         (id, data)       => request("PATCH", `/api/contratos/${id}`, data),
  renovarContrato:        (id, data)       => request("POST", `/api/contratos/${id}/renovar`, data),
  getContratosVencer:     (dias = 30)      => request("GET", `/api/contratos-proximos-vencer?dias=${dias}`),
  generarRetainerAuto:    (data = {})      => request("POST", `/api/contratos/generar-retainer`, data),
  // Cobranzas
  getAging:               (params = {})    => request("GET", `/api/cobranzas/aging?${new URLSearchParams(params)}`),
  registrarPago:          (facturaId, data)=> request("POST", `/api/facturas/${facturaId}/pago`, data),
  marcarPromesaPago:      (facturaId, data)=> request("POST", `/api/facturas/${facturaId}/promesa-pago`, data),
  // Sueldos
  getSueldos:             (params = {})    => request("GET", `/api/sueldos?${new URLSearchParams(params)}`),
  createSueldo:           (data)           => request("POST", `/api/sueldos`, data),
  // Bancos
  getBancos:              ()               => request("GET", `/api/bancos`),
  createBanco:            (data)           => request("POST", `/api/bancos`, data),
  updateBanco:            (id, data)       => request("PATCH", `/api/bancos/${id}`, data),
  getMovimientosBanco:    (params = {})    => request("GET", `/api/movimientos-banco?${new URLSearchParams(params)}`),
  createMovimientoBanco:  (data)           => request("POST", `/api/movimientos-banco`, data),
  // P&L
  getPylCliente:          (id, params={})  => request("GET", `/api/pyl/cliente/${id}?${new URLSearchParams(params)}`),
  getPylGlobal:           (params = {})    => request("GET", `/api/pyl/global?${new URLSearchParams(params)}`),
  // Forecast
  getForecastIngresos:    (meses = 3)      => request("GET", `/api/forecast/ingresos?meses=${meses}`),
  // Calendario fiscal
  getCalendarioFiscal:    ()               => request("GET", `/api/calendario-fiscal`),

  // ── Proyectos ─────────────────────────────────────────────────────────────
  getProyectos: (params = {}) => request("GET", `/api/proyectos?${new URLSearchParams(params)}`),
  getProyecto:  (id)          => request("GET", `/api/proyectos/${id}`),

  // ── Clientes ──────────────────────────────────────────────────────────────
  getClientes:    (params = {}) => request("GET", `/api/clientes?${new URLSearchParams(params)}`),
  getCliente:     (id)          => request("GET", `/api/clientes/${id}`),
  createCliente:  (data)        => request("POST", `/api/clientes`, data),
  updateCliente:  (id, data)    => request("PATCH", `/api/clientes/${id}`, data),

  // ── Agencia (singleton config) — CEO/COO/CFO only ────────────────────────
  getAgencia:     ()            => request("GET",   `/api/agencia`),
  updateAgencia:  (data)        => request("PATCH", `/api/agencia`, data),

  // ── RRHH (legajos + contratos laborales del equipo) — CEO/COO/CFO only ───
  getLegajo:           (usuarioId)      => request("GET",   `/api/rrhh/legajo/${usuarioId}`),
  upsertLegajo:        (data)           => request("POST",  `/api/rrhh/legajo`, data),
  getContratosLaborales: (params = {})  => request("GET",   `/api/rrhh/contratos-laborales?${new URLSearchParams(params)}`),
  createContratoLaboral: (data)         => request("POST",  `/api/rrhh/contratos-laborales`, data),
  updateContratoLaboral: (id, data)     => request("PATCH", `/api/rrhh/contratos-laborales/${id}`, data),

  // ── Crear Proyecto ────────────────────────────────────────────────────────
  createProyecto: (data)        => request("POST", `/api/proyectos`, data),
  updateProyecto: (id, data)    => request("PATCH", `/api/proyectos/${id}`, data),

  // ── Equipo ────────────────────────────────────────────────────────────────
  getEquipo:           ()          => request("GET",    `/api/equipo`),
  createMiembroEquipo: (data)      => request("POST",   `/api/equipo`, data),
  updateMiembroEquipo: (id, data)  => request("PATCH",  `/api/equipo/${id}`, data),
  deleteMiembroEquipo: (id)        => request("DELETE", `/api/equipo/${id}`),

  // ── Grupos (PDP / Extras / Campañas) ──────────────────────────────────────
  getGrupos:    (params = {}) => request("GET", `/api/grupos?${new URLSearchParams(params)}`),
  getGrupo:     (id)          => request("GET", `/api/grupos/${id}`),
  createGrupo:  (data)        => request("POST", `/api/grupos`, data),
  updateGrupo:  (id, data)    => request("PATCH", `/api/grupos/${id}`, data),
  reorderGrupo: (id, orden)   => request("POST", `/api/grupos/${id}/reorder`, { orden }),
  deleteGrupo:  (id, opts)    => request("DELETE", `/api/grupos/${id}?hard=${opts?.hard ? "1" : "0"}`, opts || {}),
  restoreGrupo: (id)          => request("POST", `/api/grupos/${id}/restore`, {}),

  // ── Subtareas · métricas (modelo unificado, lee desde tareas hijas) ──
  subMetrics:      (params = {})      => request("GET",    `/api/subtareas/metrics?${new URLSearchParams(params)}`),
  // Compat: lista de subtareas asignadas a un user (devuelve tareas-hijas con tarea_nombre del padre)
  getMisSubtareas: (params = {})      => request("GET",    `/api/mis-subtareas?${new URLSearchParams(params)}`),

  // ── Tareas hijas (modelo unificado, jun 2026) ──────────────────────────
  getChildren:     (parentId)         => request("GET",    `/api/tareas/${parentId}/children`),
  createChild:     (parentId, data)   => request("POST",   `/api/tareas/${parentId}/children`, data),
  reorderChildren: (parentId, ids)    => request("POST",   `/api/tareas/${parentId}/children/reorder`, { ids }),

  // ── Facturación ───────────────────────────────────────────────────────────
  getFacturas:        (params = {}) => request("GET",  `/api/facturas?${new URLSearchParams(params)}`),
  getFactura:         (id)          => request("GET",  `/api/facturas/${id}`),
  createFactura:      (data)        => request("POST", `/api/facturas`, data),
  updateFactura:      (id, data)    => request("PATCH",`/api/facturas/${id}`, data),
  emitirFactura:      (id, numero)  => request("POST", `/api/facturas/${id}/emitir`, { numero }),
  marcarPagada:       (id, fechaPago)=> request("POST", `/api/facturas/${id}/pagada`, { fecha_pago: fechaPago }),
  generarRetainerMes: (mes)         => request("POST", `/api/facturas/retainer-mensual`, { mes }),
  getCobranzasResumen:()             => request("GET",  `/api/cobranzas/resumen`),

  getPresupuestos:    ()             => request("GET",  `/api/presupuestos`),
  createPresupuesto:  (data)         => request("POST", `/api/presupuestos`, data),

  getGastos:          (params = {})  => request("GET",  `/api/gastos?${new URLSearchParams(params)}`),
  createGasto:        (data)         => request("POST", `/api/gastos`, data),

  // ── Banco de Recursos (el "cerebro" por cliente) ─────────────────────────
  getRecursosCatalogo:  ()                  => request("GET", `/api/recursos/catalogo`),
  getRecursosCliente:   (clienteId)         => request("GET", `/api/recursos/${clienteId}`),
  initBancoCliente:     (clienteId)         => request("POST", `/api/recursos/${clienteId}/init`),
  syncRecursosCliente:  (clienteId)         => request("POST", `/api/recursos/${clienteId}/sync`),
  logoCandidatos:       (clienteId)         => request("GET", `/api/recursos/${clienteId}/logo-candidatos`),
  logoAplicar:          (clienteId, driveFileId) => request("POST", `/api/recursos/${clienteId}/logo-aplicar`, { driveFileId }),
  generarADN:           (clienteId, modo)        => request("POST", `/api/clientes/${clienteId}/adn/generar`, { modo }),
  adnHistorial:         (clienteId, campo)       => request("GET", `/api/clientes/${clienteId}/adn-historial?campo=${campo}`),
  updateRecursoItem:    (instanciaId, data) => request("PATCH", `/api/recursos/item/${instanciaId}`, data),
  getAIContextCliente:  (clienteId)         => request("GET", `/api/recursos/${clienteId}/ai-context`),

  // ── Time Tracking ─────────────────────────────────────────────────────────
  getTimeEntries:   (params = {}) => request("GET", `/api/time-tracking?${new URLSearchParams(params)}`),
  createTimeEntry:  (data)        => request("POST", `/api/time-tracking`, data),
  startTimer:       (data)        => request("POST", `/api/time-tracking/start`, data),
  stopTimer:        (data)        => request("POST", `/api/time-tracking/stop`, data),
  getActiveTimer:   (usuario_id)  => request("GET",  `/api/time-tracking/active?usuario_id=${encodeURIComponent(usuario_id)}`),

  // ── Dashboard ─────────────────────────────────────────────────────────────
  getDashboard: () => request("GET", `/api/dashboard`),

  // ── Status Semanal v6.9 ──────────────────────────────────────────────────
  getStatusSemana:    (clienteId, params = {}) => request("GET", `/api/status-semana/${clienteId}?${new URLSearchParams(params)}`),
  getStatusSemanaEC:  (ecId, params = {})      => request("GET", `/api/status-semana/ec/${ecId}?${new URLSearchParams(params)}`),

  // ── Coberturas · Chat interno v7.5.3 ─────────────────────────────────────
  getCoberturaChat:     (id)              => request("GET",    `/api/coberturas/${id}/chat`),
  postCoberturaChat:    (id, data)        => request("POST",   `/api/coberturas/${id}/chat`, data),
  deleteCoberturaChat:  (id, msgId)       => request("DELETE", `/api/coberturas/${id}/chat/${msgId}`),

  // ── Feed Preview v6.8 ────────────────────────────────────────────────────
  getFeed:              (clienteId, params = {}) => request("GET", `/api/feed/${clienteId}?${new URLSearchParams(params)}`),
  getFeedVersiones:     (clienteId, params = {}) => request("GET", `/api/feed/${clienteId}/versions?${new URLSearchParams(params)}`),
  guardarFeedVersion:   (clienteId, data)        => request("POST", `/api/feed/${clienteId}/version`, data),
  actualizarFeedVersion:(id, data)               => request("PATCH", `/api/feed/version/${id}`, data),
  reordenarFeed:        (clienteId, data)        => request("POST", `/api/feed/${clienteId}/reorder`, data),
  setTareaPreview:      (tareaId, data)          => request("POST", `/api/feed/tarea/${tareaId}/preview`, data),
  detectTareaPreview:   (tareaId, force = false) => request("POST", `/api/feed/tarea/${tareaId}/preview/detect`, { force }),

  // ── Pendientes de publicar v7.1 F1.2 ─────────────────────────────────────
  getPendientesPublicar: () => request("GET", `/api/mi/pendientes-publicar`),

  // ── Moodboards v7.1 M1 ───────────────────────────────────────────────────
  getMoodboards:     (scope, anchorId) => request("GET", `/api/moodboards?scope=${encodeURIComponent(scope)}&anchor_id=${encodeURIComponent(anchorId)}`),
  getMoodboard:      (id)              => request("GET", `/api/moodboards/${id}`),
  upsertMoodboard:   (data)            => request("POST", `/api/moodboards/get-or-create`, data),
  updateMoodboard:   (id, data)        => request("PATCH", `/api/moodboards/${id}`, data),
  addMoodboardItem:  (id, data)        => request("POST", `/api/moodboards/${id}/items`, data),
  updateMoodboardItem: (itemId, data)  => request("PATCH", `/api/moodboards/items/${itemId}`, data),
  reorderMoodboard:  (id, ordenIds)    => request("POST", `/api/moodboards/${id}/reorder`, { orden_ids: ordenIds }),
  removeMoodboardItem: (itemId)        => request("DELETE", `/api/moodboards/items/${itemId}`),
  shareMoodboard:    (id, data={})     => request("POST", `/api/moodboards/${id}/share`, data),
  revokeMoodboardShare: (id)           => request("DELETE", `/api/moodboards/${id}/share`),
  listMoodboardComments: (id)          => request("GET", `/api/moodboards/${id}/comentarios`),
  addMoodboardComment:   (id, data)    => request("POST", `/api/moodboards/${id}/comentarios`, data),

  // ── Cobertura checklist v7.1 F4 ──────────────────────────────────────────
  getCoberturaChecklist: (id)            => request("GET",   `/api/coberturas/${id}/checklist`),
  setChecklistItem:      (id, data)      => request("PATCH", `/api/coberturas/${id}/checklist`, data),

  // ── F2 Templates ─────────────────────────────────────────────────────────
  listTemplates:         ()              => request("GET",   `/api/templates`),
  getTemplate:           (id)            => request("GET",   `/api/templates/${id}`),
  validateTemplate:      (id, contenido) => request("POST",  `/api/templates/${id}/validate`, { contenido }),
  generarTemplate:       (id, data)      => request("POST",  `/api/templates/${id}/generar`, data),

  // ── F6 Saturación marcas ─────────────────────────────────────────────────
  getMisMarcas:          ()              => request("GET",   `/api/mi/saturacion-marcas`),

  // ── v7.2 · Sorteos ───────────────────────────────────────────────────────
  listSorteos:           (params)        => request("GET",   `/api/sorteos${params?.cliente_id ? `?cliente_id=${params.cliente_id}` : ""}`),
  createSorteo:          (data)          => request("POST",  `/api/sorteos`, data),
  getSorteo:             (id)            => request("GET",   `/api/sorteos/${id}`),
  updateSorteo:          (id, data)      => request("PATCH", `/api/sorteos/${id}`, data),
  listParticipantes:     (id)            => request("GET",   `/api/sorteos/${id}/participantes`),
  // v7.2.3 · CRUD participantes
  clearParticipantes:    (id)            => request("DELETE",`/api/sorteos/${id}/participantes`),
  deleteParticipante:    (id, pid)       => request("DELETE",`/api/sorteos/${id}/participantes/${pid}`),
  updateParticipante:    (id, pid, data) => request("PATCH", `/api/sorteos/${id}/participantes/${pid}`, data),
  addPremio:             (id, data)      => request("POST",  `/api/sorteos/${id}/premios`, data),
  deletePremio:          (id, pid)       => request("DELETE",`/api/sorteos/${id}/premios/${pid}`),
  drawPremio:            (id, pid)       => request("POST",  `/api/sorteos/${id}/draw/${pid}`),
  undoPremio:            (id, pid)       => request("POST",  `/api/sorteos/${id}/draw/${pid}/undo`),
  // v7.2.1 · modo manual + dry + no contactado + lifecycle
  startSpinPremio:       (id, pid)       => request("POST",  `/api/sorteos/${id}/spin/${pid}/start`),
  cancelSpinPremio:      (id, pid)       => request("POST",  `/api/sorteos/${id}/spin/${pid}/cancel`),
  dryDrawSorteo:         (id)            => request("POST",  `/api/sorteos/${id}/dry-draw`),
  noContactadoPremio:    (id, pid, motivo) => request("POST",`/api/sorteos/${id}/draw/${pid}/no-contactado`, { motivo }),
  deleteSorteo:          (id)            => request("DELETE",`/api/sorteos/${id}`),
  duplicarSorteo:        (id, nombre)    => request("POST",  `/api/sorteos/${id}/duplicar`, { nombre }),
  // Upload CSV multipart (no usa request())
  async uploadSorteoCSV(id, file) {
    const fd = new FormData();
    fd.append("file", file);
    const token = await getIdToken();
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (KEY) headers["x-api-key"] = KEY;
    const res = await fetch(`${BASE}/api/sorteos/${id}/upload-csv`, { method: "POST", headers, body: fd });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Upload falló (${res.status}): ${txt.slice(0, 200)}`);
    }
    return await res.json();
  },
  analyzeMoodboard:      (id)          => request("POST", `/api/moodboards/${id}/analyze`),
  // Upload no usa request() (multipart): construye manual con fetch
  async uploadMoodboardFiles(id, files, opts = {}) {
    const fd = new FormData();
    for (const f of files) fd.append("files", f);
    if (opts.tag) fd.append("tag", opts.tag);
    if (opts.caption) fd.append("caption", opts.caption);
    const token = await getIdToken();
    const headers = {};
    if (token) headers["Authorization"] = `Bearer ${token}`;
    if (KEY) headers["x-api-key"] = KEY;
    const res = await fetch(`${BASE}/api/moodboards/${id}/upload`, { method: "POST", headers, body: fd });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`Upload falló (${res.status}): ${txt.slice(0, 200)}`);
    }
    return await res.json();
  },


  // ── Gamificación v6.7 ────────────────────────────────────────────────────
  getGamifProfile:    (userId)      => request("GET",  `/api/gamification/profile/${userId}`),
  getGamifCatalogos:  ()            => request("GET",  `/api/gamification/catalogos`),
  getGamifPremios:    ()            => request("GET",  `/api/gamification/premios`),
  getLeaderboard:     (params = {}) => request("GET",  `/api/gamification/leaderboard?${new URLSearchParams(params)}`),
  getRetoSemanal:     ()            => request("GET",  `/api/gamification/reto-semanal`),
  aprobarPremio:      (id)          => request("POST", `/api/gamification/premios/${id}/aprobar`),
  entregarPremio:     (id, notas)   => request("POST", `/api/gamification/premios/${id}/entregar`, { notas }),

  // ── Coberturas v2 (eventos) ──────────────────────────────────────────────
  getCoberturas:      (params = {}) => request("GET",    `/api/coberturas?${new URLSearchParams(params)}`),
  getCobertura:       (id)          => request("GET",    `/api/coberturas/${id}`),
  getMisEsperasCliente: () => request("GET", "/api/mi-dia/espera-cliente"),
  getMisCoberturasAgenda: (fecha_desde, fecha_hasta) =>
    request("GET", `/api/coberturas/mi-agenda?fecha_desde=${fecha_desde}&fecha_hasta=${fecha_hasta}`),
  // v8.6.22 · Fase 10 (Tier 2) — sugerencia de crew (dry-run, mismo patrón
  // que candidatosAsignacion de tareas).
  getSugerenciaCrew:  (id)          => request("GET",    `/api/coberturas/${id}/sugerencia-crew`),
  aplicarSugerenciaCrew: (id)       => request("POST",   `/api/coberturas/${id}/sugerencia-crew/aplicar`),

  // ── Producciones (Coberturas) ─────────────────────────────────────────────
  getProducciones:    (params = {}) => request("GET",    `/api/producciones?${new URLSearchParams(params)}`),
  getProduccion:      (id)          => request("GET",    `/api/producciones/${id}`),
  createProduccion:   (data)        => request("POST",   `/api/producciones`, data),
  updateProduccion:   (id, data)    => request("PATCH",  `/api/producciones/${id}`, data),
  deleteProduccion:   (id)          => request("DELETE", `/api/producciones/${id}`),

  // ── Notifications ─────────────────────────────────────────────────────────
  getNotifications:    (params = {}) => request("GET",   `/api/notifications?${new URLSearchParams(params)}`),
  markNotifRead:       (id)          => request("PATCH", `/api/notifications/${id}`, { leida: true }),
  markAllNotifsRead:   (userId)      => request("POST",  `/api/notifications/mark-all-read`, { user_id: userId }),
  deleteNotification:  (id)          => request("DELETE",`/api/notifications/${id}`),
  clearReadNotifs:     (userId)      => request("POST",  `/api/notifications/clear-read`, { user_id: userId }),

  // ── GHL (GoHighLevel) Pipeline ────────────────────────────────────────────
  getGhlContacts:  (params = {}) => request("GET",  `/api/ghl/contacts?${new URLSearchParams(params)}`),
  getGhlLeads:     (params = {}) => request("GET",  `/api/ghl/leads?${new URLSearchParams(params)}`),
  getGhlPipelines: ()            => request("GET",  `/api/ghl/pipelines`),
  generarPitch:    (leadId, body = {}) => request("POST", `/api/ghl/leads/${leadId}/pitch`, body),
  moveGhlLead:     (leadId, stageId)   => request("PATCH", `/api/ghl/leads/${leadId}`, { stage: stageId }),

  // ── Webhooks de automatización (Espejo Digital flows) ─────────────────────
  triggerNuevaTarea:       (tareaId)              => request("POST", "/tasks/created",     { tareaId }),
  triggerTareaActualizada: (tareaId, field, value) => request("POST", "/tasks/updated",    { tareaId, field, value }),
  triggerClienteActivado:  (clienteId)            => request("POST", "/clientes/activated", { clienteId }),
  importarPlan:            (body)                 => request("POST", "/plan/import",       body),
  generarReporte:          (body)                 => request("POST", "/api/reports/generate",  body),
  health:                  ()                     => request("GET",  "/health"),

  // ── Reportes PRO ──────────────────────────────────────────────────────────
  generarReportePro:       (clienteId, periodo)   => request("POST", "/api/reportes/pro/generar", { clienteId, periodo }),
  getReportesArchivo:      (params = {})          => request("GET",  `/api/reportes/archivo?${new URLSearchParams(params)}`),
  reporteArchivoAbrir:     (id)                   => request("GET",  `/api/reportes/archivo/${id}/abrir`),
  reporteArchivoExcel:     (id)                   => request("GET",  `/api/reportes/archivo/${id}/excel`),
  reporteEntregablesGenerar: (body)               => request("POST", "/api/reportes/entregables/generar", body),
  grupoExportarExcel:      (grupoId)              => request("GET",  `/api/grupos/${grupoId}/exportar-excel`),
  // Pizarra digital: notas por marca (ideas, bajadas, fechas, eventos)
  pizarraListar:           (p = {})               => request("GET",  `/api/pizarra?${new URLSearchParams(p)}`),
  pizarraCrear:            (body)                 => request("POST", "/api/pizarra", body),
  pizarraActualizar:       (id, body)             => request("PATCH", `/api/pizarra/${id}`, body),
  downloadReporte:         (id)                   => `${import.meta.env.VITE_API_URL || ""}/api/reportes/pro/${id}/download`,

  // ── Risk Radar ────────────────────────────────────────────────────────────
  getRiskSnapshot:         ()                     => request("GET",  "/api/risk-radar/snapshot"),
  scoreRiskNow:            ()                     => request("POST", "/api/risk-radar/score"),
  getRiskHistorico:        (clienteId)            => request("GET",  `/api/risk-radar/cliente/${clienteId}/historico`),

  // ── Crew Brain ────────────────────────────────────────────────────────────
  brainAsk:                (pregunta)             => request("POST", "/api/brain/ask", { pregunta }),

  // ── SLA Engine ────────────────────────────────────────────────────────────
  evaluarSLA:              ()                     => request("POST", "/api/sla/evaluar"),
  getSlaBreaches:          ()                     => request("GET",  "/api/sla/breaches"),
  resolverBreach:          (id)                   => request("POST", `/api/sla/breaches/${id}/resolver`),
  getSlaDefinitions:       ()                     => request("GET",  "/api/sla/definitions"),

  // ── Audit Trail ───────────────────────────────────────────────────────────
  getAudit:                (params = {})          => request("GET",  `/api/audit?${new URLSearchParams(params)}`),
  getAuditEntidad:         (entidad, id)          => request("GET",  `/api/audit/entidad/${entidad}/${id}`),

  // ── AI Cost ───────────────────────────────────────────────────────────────
  getAICostSummary:        (params = {})          => request("GET",  `/api/ai-cost/summary?${new URLSearchParams(params)}`),

  // ── Global Search ─────────────────────────────────────────────────────────
  // opts.clientes (ids) y opts.tipos acotan la búsqueda (pantalla de Tareas); sin opts es la búsqueda universal de siempre
  search:                  (q, opts = {})         => request("GET",  `/api/search?q=${encodeURIComponent(q)}`
                              + (opts.clientes?.length ? `&clientes=${opts.clientes.map(encodeURIComponent).join(",")}` : "")
                              + (opts.tipos?.length ? `&tipos=${opts.tipos.map(encodeURIComponent).join(",")}` : "")),

  // ── RRHH ──────────────────────────────────────────────────────────────────
  rrhhDashboard:           ()                     => request("GET",  "/api/rrhh/dashboard"),
  getLegajo:               (usuarioId)            => request("GET",  `/api/rrhh/legajo/${usuarioId}`),
  saveLegajo:              (body)                 => request("POST", "/api/rrhh/legajo", body),
  getContratosLaborales:   (params = {})          => request("GET",  `/api/rrhh/contratos-laborales?${new URLSearchParams(params)}`),
  createContratoLaboral:   (body)                 => request("POST", "/api/rrhh/contratos-laborales", body),
  updateContratoLaboral:   (id, body)             => request("PATCH",`/api/rrhh/contratos-laborales/${id}`, body),
  getVacaciones:           (usuarioId)            => request("GET",  `/api/rrhh/vacaciones/${usuarioId}`),
  createVacacion:          (body)                 => request("POST", "/api/rrhh/vacaciones", body),
  getAusencias:            (params = {})          => request("GET",  `/api/rrhh/ausencias?${new URLSearchParams(params)}`),
  createAusencia:          (body)                 => request("POST", "/api/rrhh/ausencias", body),
  getAguinaldo:            (usuarioId, anio)      => request("GET",  `/api/rrhh/aguinaldo/${usuarioId}/${anio}`),
  devengarAguinaldoMes:    (anio, mes)            => request("POST", "/api/rrhh/aguinaldo/devengar-mes", { anio, mes }),
  calcularLiquidacion:     (body)                 => request("POST", "/api/rrhh/liquidacion/calcular", body),
  calcularIndemnizacion:   (usuarioId, body = {}) => request("POST", `/api/rrhh/indemnizacion/${usuarioId}`, body),
  getAdelantos:            (params = {})          => request("GET",  `/api/rrhh/adelantos?${new URLSearchParams(params)}`),
  createAdelanto:          (body)                 => request("POST", "/api/rrhh/adelantos", body),
  getPerformanceReviews:   (usuarioId)            => request("GET",  `/api/rrhh/performance-reviews${usuarioId ? "/" + usuarioId : ""}`),
  createPerformanceReview: (body)                 => request("POST", "/api/rrhh/performance-reviews", body),
  getCapacitaciones:       (params = {})          => request("GET",  `/api/rrhh/capacitaciones?${new URLSearchParams(params)}`),
  createCapacitacion:      (body)                 => request("POST", "/api/rrhh/capacitaciones", body),
  getOnboardingChecklist:  ()                     => request("GET",  "/api/rrhh/onboarding-checklist"),

  // ── Costos Operativos ─────────────────────────────────────────────────────
  getCostosOperativos:     (params = {})          => request("GET",  `/api/costos-operativos?${new URLSearchParams(params)}`),
  createCostoOperativo:    (body)                 => request("POST", "/api/costos-operativos", body),
  updateCostoOperativo:    (id, body)             => request("PATCH",`/api/costos-operativos/${id}`, body),
  getCategoriasCostos:     ()                     => request("GET",  "/api/costos-operativos/categorias"),
  getRunRate:              (usdToPyg)             => request("GET",  `/api/costos-operativos/run-rate?usdToPyg=${usdToPyg || 7300}`),
  getProximosVencimientos: (dias = 30)            => request("GET",  `/api/costos-operativos/proximos-vencimientos?dias=${dias}`),
  getPresupuestoVsReal:    (anio)                 => request("GET",  `/api/costos-operativos/presupuesto-vs-real/${anio}`),
  getAllocacionClientes:   (periodo)              => request("GET",  `/api/costos-operativos/allocacion-clientes?periodo=${periodo}`),
  getProveedores:          ()                     => request("GET",  "/api/proveedores"),
  createProveedor:         (body)                 => request("POST", "/api/proveedores", body),
  getCentrosCosto:         ()                     => request("GET",  "/api/centros-costo"),
  createCentroCosto:       (body)                 => request("POST", "/api/centros-costo", body),
  getPresupuestosAnuales:  (anio)                 => request("GET",  `/api/presupuestos-anuales/${anio}`),
  createPresupuestoAnual:  (body)                 => request("POST", "/api/presupuestos-anuales", body),

  // ── RBAC + ABAC ───────────────────────────────────────────────────────────
  getRoles:                ()                     => request("GET",  "/api/roles"),
  getRole:                 (id)                   => request("GET",  `/api/roles/${id}`),
  createRole:              (body)                 => request("POST", "/api/roles", body),
  updateRole:              (id, body)             => request("PATCH",`/api/roles/${id}`, body),
  deleteRole:              (id)                   => request("DELETE",`/api/roles/${id}`),
  setRolePermissions:      (id, permissionIds)    => request("PUT",  `/api/roles/${id}/permissions`, { permissionIds }),
  getPermissions:          (params = {})          => request("GET",  `/api/permissions?${new URLSearchParams(params)}`),
  getPermissionModulos:    ()                     => request("GET",  "/api/permissions/modulos"),
  getUserOverrides:        (userId)               => request("GET",  `/api/user-overrides/${userId}`),
  createUserOverride:      (body)                 => request("POST", "/api/user-overrides", body),
  deleteUserOverride:      (id)                   => request("DELETE",`/api/user-overrides/${id}`),
  getMyPermissions:        ()                     => request("GET",  "/api/me/permissions"),
  enviarLinkApp:           ()                     => request("POST", "/api/me/enviar-link-app", {}),
  getResourceAccess:       (params = {})          => request("GET",  `/api/resource-access?${new URLSearchParams(params)}`),
  createResourceAccess:    (body)                 => request("POST", "/api/resource-access", body),
  deleteResourceAccess:    (id)                   => request("DELETE",`/api/resource-access/${id}`),

  // ── Agency Operating System PRO ───────────────────────────────────────────
  // BUs
  getBUs:                  ()                     => request("GET",  "/api/bus"),
  createBU:                (body)                 => request("POST", "/api/bus", body),
  updateBU:                (id, body)             => request("PATCH",`/api/bus/${id}`, body),

  // AGI
  getAGICascada:           (params = {})          => request("GET",  `/api/agi/cascada?${new URLSearchParams(params)}`),
  getAGIPorBU:             (params = {})          => request("GET",  `/api/agi/por-bu?${new URLSearchParams(params)}`),
  getAGIPorCliente:        (params = {})          => request("GET",  `/api/agi/por-cliente?${new URLSearchParams(params)}`),
  getAGIConcentracion:     (params = {})          => request("GET",  `/api/agi/concentracion?${new URLSearchParams(params)}`),

  // Pass-through
  getPassThrough:          (params = {})          => request("GET",  `/api/pass-through?${new URLSearchParams(params)}`),
  createPassThrough:       (body)                 => request("POST", "/api/pass-through", body),
  updatePassThrough:       (id, body)             => request("PATCH",`/api/pass-through/${id}`, body),

  // Media Ops
  getMediaDashboard:       ()                     => request("GET",  "/api/media/dashboard"),
  getMediaPlans:           (params = {})          => request("GET",  `/api/media/plans?${new URLSearchParams(params)}`),
  createMediaPlan:         (body)                 => request("POST", "/api/media/plans", body),
  updateMediaPlan:         (id, body)             => request("PATCH",`/api/media/plans/${id}`, body),
  getMediaOrders:          (params = {})          => request("GET",  `/api/media/orders?${new URLSearchParams(params)}`),
  createMediaOrder:        (body)                 => request("POST", "/api/media/orders", body),
  getMediaSpend:           (params = {})          => request("GET",  `/api/media/spend?${new URLSearchParams(params)}`),
  createMediaSpend:        (body)                 => request("POST", "/api/media/spend", body),
  reconciliarMediaPlan:    (planId)               => request("GET",  `/api/media/reconcile/${planId}`),
  getTrustAccount:         (clienteId)            => request("GET",  `/api/media/trust/${clienteId}`),
  depositarTrust:          (body)                 => request("POST", "/api/media/trust/deposito", body),

  // Operational KPIs
  getOpsDashboard:         (params = {})          => request("GET",  `/api/ops/dashboard?${new URLSearchParams(params)}`),
  getUtilization:          (usuarioId, periodo)   => request("GET",  `/api/ops/utilization/${usuarioId}?periodo=${periodo || ""}`),
  snapshotUtilization:     (periodo)              => request("POST", "/api/ops/snapshot-utilization", { periodo }),
  getWIP:                  ()                     => request("GET",  "/api/ops/wip"),
  getDSO:                  ()                     => request("GET",  "/api/ops/dso"),

  // Governance
  getScopeCreep:           (params = {})          => request("GET",  `/api/governance/scope-creep?${new URLSearchParams(params)}`),
  getChangeOrders:         (params = {})          => request("GET",  `/api/change-orders?${new URLSearchParams(params)}`),
  createChangeOrder:       (body)                 => request("POST", "/api/change-orders", body),
  updateChangeOrder:       (id, body)             => request("PATCH",`/api/change-orders/${id}`, body),
  getDeferredRevenue:      ()                     => request("GET",  "/api/deferred-revenue"),
  createDeferredRevenue:   (body)                 => request("POST", "/api/deferred-revenue", body),

  // Pitches
  getPitches:              (params = {})          => request("GET",  `/api/pitches?${new URLSearchParams(params)}`),
  createPitch:             (body)                 => request("POST", "/api/pitches", body),
  updatePitch:             (id, body)             => request("PATCH",`/api/pitches/${id}`, body),
  getPitchMetricas:        ()                     => request("GET",  "/api/pitches/metricas"),

  // ── CCC Coordinación + Smart QC ───────────────────────────────────────────
  cccDashboard:            ()                     => request("GET",  "/api/ccc/dashboard"),
  qcValidar:               (tareaId)              => request("POST", `/api/qc/validar/${tareaId}`),
  getQCsTarea:             (tareaId)              => request("GET",  `/api/qc/tarea/${tareaId}`),

  // ── Costeo (cost-per-hour real) ───────────────────────────────────────────
  getCostRate:             (usuarioId, periodo)   => request("GET",  `/api/costeo/usuario/${usuarioId}?periodo=${periodo || ""}`),
  snapshotCosteoMensual:   (periodo)              => request("POST", "/api/costeo/snapshot-mensual", { periodo }),
  getResumenCostosTarifas: ()                     => request("GET",  "/api/costeo/resumen-vs-tarifas"),
};
